/**
 * @license
 * Copyright (c) %FIRST_YEAR% %CURRENT_YEAR%, Oracle and/or its affiliates.
 * The Universal Permissive License (UPL), Version 1.0
 * as shown at https://oss.oracle.com/licenses/upl/
 * @ignore
 */
import type { Cell } from '#UNSAFE_TableView';
import type { ActiveCell, TableColumn } from '../Table';

import { getCellType } from '#PRIVATE_TableCommon/utils/DomUtils';
import {
  getElementFromCell,
  logicalCellExtractor,
  TABLE_FOCUSABLE_SELECTOR
} from './TableDomUtils';

/**
 * Browser zoom levels are not handled identically between Range sizes and Element sizes, so this offset
 * should be used when comparing horizontal widths. Using a smaller value (0.01 for example) causes false
 * positives, while using a larger value (0.05 for example) causes actual truncations to be missed.
 */
const HORIZONTAL_TRUNCATION_OFFSET = 0.015;

/**
 * Default Redwood line-height overrides cause false positives as the Range sizes are always for a 'normal'
 * line-height, and none of the default scale fonts have 'normal' line-height values. This causes offsets
 * of between 0.5px and 1.5px. As a true truncation will cause an offset of closer to a full line height
 * (at least 12px for 2xs text), use a larger offset value than the currently observed 1.5px max for safety.
 */
const VERTICAL_TRUNCATION_OFFSET = 5;

/**
 * Helper method to determine if a given cell contains truncated text.
 */
export const hasTruncatedText = (cellElement: HTMLElement) => {
  if (cellElement.getAttribute('data-oj-table-tooltip') !== 'enabled') {
    return false;
  }
  const innerText = String(cellElement.innerText).trim();
  if (innerText === '') {
    return false;
  }

  const walker = document.createTreeWalker(cellElement, NodeFilter.SHOW_TEXT);
  while (walker.nextNode()) {
    const range = new Range();
    const textNode = walker.currentNode;
    range.selectNodeContents(textNode);
    const untruncatedTextLength = range.getBoundingClientRect().width;
    const untruncatedTextHeight = range.getBoundingClientRect().height;

    let firstParent = true;
    let parentElement = textNode.parentElement;
    while (parentElement != null) {
      const parentStyles = getComputedStyle(parentElement);
      if (firstParent) {
        // only verify truncation status of 'visible' text nodes
        if (untruncatedTextLength === 0 || parentStyles.visibility === 'hidden') {
          break;
        }
        firstParent = false;
      }
      const horizontalPadding =
        parseFloat(parentStyles.paddingLeft) + parseFloat(parentStyles.paddingRight);
      const horizontalBorder =
        parseFloat(parentStyles.borderLeft) + parseFloat(parentStyles.borderRight);
      const renderedTextLength =
        parentElement.getBoundingClientRect().width - horizontalPadding - horizontalBorder;

      const verticalPadding =
        parseFloat(parentStyles.paddingTop) + parseFloat(parentStyles.paddingBottom);
      const verticalBorder =
        parseFloat(parentStyles.borderTop) + parseFloat(parentStyles.borderBottom);
      const renderedTextHeight =
        parentElement.getBoundingClientRect().height - verticalPadding - verticalBorder;

      if (
        untruncatedTextLength - renderedTextLength > HORIZONTAL_TRUNCATION_OFFSET ||
        untruncatedTextHeight - renderedTextHeight > VERTICAL_TRUNCATION_OFFSET
      ) {
        return true;
      }
      if (parentElement === cellElement) {
        break;
      }
      parentElement = parentElement.parentElement;
    }
  }
  return false;
};

/**
 * Helper function to handle updating the Table's tooltip due to a focus navigation gesture.
 */
export function handleNavigationTooltipGesture<K, C>(
  rootElement: HTMLDivElement,
  tableId: string,
  cell: ActiveCell<K, C>,
  showTooltip: (cell: Cell<K, C>) => void,
  hideTooltip: (isImmediate?: boolean) => void
) {
  hideTooltip(true);
  if (cell.type === 'data' || cell.type === 'header' || cell.type === 'footer') {
    const cellElement = getElementFromCell(rootElement, tableId, cell);
    if (cellElement != null) {
      showTooltip(cell);
    }
  }
}

/**
 * Helper function to handle updating the Table's tooltip due to a pointer move gesture.
 */
export function handleMoveTooltipGesture<K extends string | number, D, C extends string>(
  tableId: string,
  columnsArray: TableColumn<K, D, C>[],
  event: PointerEvent,
  showTooltip: (cell: Cell<K, C>) => void,
  hideTooltip: (isImmediate?: boolean) => void,
  tooltipId: string
) {
  let tooltipShown = false;
  const targetElement = event.relatedTarget as HTMLElement;
  if (targetElement != null && targetElement.closest(`#${tooltipId}`) != null) {
    return;
  }
  const cellElement = (event.target as HTMLElement).closest(
    `[${TABLE_FOCUSABLE_SELECTOR}='${tableId}']`
  ) as HTMLElement;
  if (cellElement != null) {
    const type = getCellType(cellElement);
    if (type === 'data' || type === 'header' || type === 'footer') {
      showTooltip(logicalCellExtractor<K, D, C>(cellElement, tableId, columnsArray) as Cell<K, C>);
      tooltipShown = true;
    }
  }
  if (!tooltipShown) {
    hideTooltip();
  }
}

/**
 * Helper function to handle updating the Table's tooltip due to a pointer leave gesture.
 */
export const handleLeaveTableTooltipGesture = (
  rootElement: HTMLDivElement,
  event: PointerEvent,
  hideTooltip: (isImmediate?: boolean) => void,
  tooltipId: string
) => {
  const targetElement = event.relatedTarget as HTMLElement;
  if (targetElement != null && targetElement.closest(`#${tooltipId}`) != null) {
    // the pointer 'left' the table, but is only over a cell tooltip - in most cases, we want
    // to just return here as the pointer is still 'over' the table. however, this can also
    // occur when the pointer is technically outside of the table's bounds, and in that case
    // we don't actually want to return as we should clear out our cell tooltip as expected
    const boundingRect = rootElement.getBoundingClientRect();
    const isOutOfBounds =
      event.clientX < boundingRect.left ||
      event.clientX > boundingRect.right ||
      event.clientY < boundingRect.top ||
      event.clientY > boundingRect.bottom;
    if (!isOutOfBounds) {
      return;
    }
  }
  hideTooltip();
};
